import React, { useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView as useInViewHook } from 'react-intersection-observer';
import { Code, Users, Award, Lightbulb, Target, Rocket, Bot } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getExperienceFormats, getTotalCertifications, getTotalAwards } from '../utils/dynamicStats';
import { experiences, patents } from '../data/mockData';

// Xcode default theme token colors, pixel-sampled from Xcode's own Settings > Themes
// panel AND from this exact snippet rendered live in Xcode (light/dark), so the site
// matches Xcode's real heuristic highlighter rather than a generic assumption:
//   - declared type name (after class/struct/enum/protocol)  -> Type Declarations
//   - declared property/constant/function name (after let/var/func) -> Other Declarations
//   - known stdlib/Foundation type (String, Int, Bool, ...)  -> Other Type Names
//   - any other capitalized type reference (assumed project-local) -> Project Type Names
const XCODE_LIGHT_COLORS = {
  keyword: '#A0459F',
  declType: '#296188',
  declName: '#367AAB',
  stdlibType: '#4624A9',
  projectType: '#31565B',
  string: '#C13E2A',
  number: '#2729CF',
  comment: '#737F8B',
};
const XCODE_DARK_COLORS = {
  keyword: '#EE81B0',
  declType: '#89DDFB',
  declName: '#69AEC8',
  stdlibType: '#D5BCFA',
  projectType: '#BBF0E4',
  string: '#EF8876',
  number: '#D6CA86',
  comment: '#818C97',
};
const STDLIB_TYPES = 'String|Int|Double|Float|Bool|Character|Array|Dictionary|Set|Void|Any|AnyObject|CGFloat|URL|Data|Date|Error';
const SWIFT_TOKEN_REGEX = new RegExp(
  '("(?:[^"\\\\]|\\\\.)*")' + // 1 string
  '|(\\/\\/.*$)' + // 2 comment
  '|(\\b(?:class|struct|enum|protocol|extension|import|let|var|func|return|if|else|guard|switch|case|default|for|while|in|self|Self|true|false|nil|static|private|public|internal|fileprivate|open|final)\\b)' + // 3 keyword
  '|(?<=\\b(?:class|struct|enum|protocol|extension)\\s)([A-Z][A-Za-z0-9_]*)' + // 4 declared type name
  '|(?<=\\b(?:let|var|func)\\s)([a-z_][A-Za-z0-9_]*)' + // 5 declared property/function name
  `|(\\b(?:${STDLIB_TYPES})\\b)` + // 6 known stdlib type
  '|(\\b[A-Z][A-Za-z0-9_]*\\b)' + // 7 other (assumed project-local) type reference
  '|(\\b\\d+(?:\\.\\d+)?\\b)', // 8 number
  'gm'
);

const tokenizeSwiftLine = (line, colors, lineKey) => {
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  SWIFT_TOKEN_REGEX.lastIndex = 0;
  while ((match = SWIFT_TOKEN_REGEX.exec(line)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<React.Fragment key={`${lineKey}-${key++}`}>{line.slice(lastIndex, match.index)}</React.Fragment>);
    }
    const color = match[1] ? colors.string
      : match[2] ? colors.comment
      : match[3] ? colors.keyword
      : match[4] ? colors.declType
      : match[5] ? colors.declName
      : match[6] ? colors.stdlibType
      : match[7] ? colors.projectType
      : colors.number;
    parts.push(<span key={`${lineKey}-${key++}`} style={{ color }}>{match[0]}</span>);
    lastIndex = SWIFT_TOKEN_REGEX.lastIndex;
  }
  if (lastIndex < line.length) {
    parts.push(<React.Fragment key={`${lineKey}-${key++}`}>{line.slice(lastIndex)}</React.Fragment>);
  }
  return parts;
};

const renderSwiftCode = (code, colors) => {
  const lines = code.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {tokenizeSwiftLine(line, colors, i)}
      {i < lines.length - 1 ? '\n' : ''}
    </React.Fragment>
  ));
};

const AboutSection = () => {
  const { isDark, background, text, accents } = useTheme();
  const controls = useAnimation();
  const cardsRef = useRef([]);
  const [sectionRef, inView] = useInViewHook({
    threshold: 0,
    triggerOnce: true
  });
  
  // Dynamic statistics
  const experienceFormats = getExperienceFormats();
  const totalCertifications = getTotalCertifications();
  const totalAwards = getTotalAwards();
  const applicationsDeployed = "10+"; // Dynamic applications count
  const totalPatents = patents.length;
  const xcodeColors = isDark ? XCODE_DARK_COLORS : XCODE_LIGHT_COLORS;
  const swiftCode = `class RahulSingh: PrincipalSoftwareEngineer {
    let experience = ${experienceFormats.code}
    let applicationsDeployed = "${applicationsDeployed}"
    let certifications = "${totalCertifications}"
    let awardsEarned = "${totalAwards}"${totalPatents >= 1 ? `
    let patentsFiled = "${totalPatents}"` : ''}

    let platformSpecialization = [
        "iOS", "iPadOS", "macOS", "Web"
    ]

    let languageSpecialization = [
        "Swift", "Objective-C", "javascript", "C/C++"
    ]

    let frameworkUsed = [
        "SwiftUI", "UIKit", "AppKit", "CoreData",
        "MapKit", "CoreLocation", "AVFoundation",
        "CoreML"
    ]

    let agenticToolsUsed = [
        "GitHub Copilot", "Claude", "Claude Code",
        "ChatGPT", "Codex"
    ]

    let leadership = true
    let innovation = .coreML + .nlp + .agenticAI

    func buildAmazingApplication() -> Success {
        return .enterprise + .scalable + .innovation
    }

    var currentFocus: [String] {
        return [
            "AI/ML Integration",
            "Team Mentoring",
            "Architecture Excellence",
            "Agentic Development Workflows"
        ]
    }
}`;
  
  // Get current position information dynamically
  const currentPosition = experiences.find(exp => exp.type === 'current');
  const profileInfo = {
    title: currentPosition?.position || 'Principal Software Engineer',
    company: currentPosition?.company || 'OpenText',
    location: currentPosition?.location || 'Bengaluru, Karnataka, India'
  };

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      rotateX: 15
    },
    visible: {
      opacity: 1, 
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const cardHoverVariants = {
    hover: {
      scale: 1.05,
      rotateY: 5,
      z: 50,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    tap: {
      scale: 0.95
    }
  };

  const statsCounterVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: 0.3
      }
    }
  };

  const highlights = [
    {
      icon: Code,
      title: experienceFormats.mastery,
      description: "Proficient in Swift, SwiftUI, Objective-C, with SOLID design focus",
      color: "from-blue-400 to-blue-600",
      glow: "shadow-blue-500/20"
    },
    {
      icon: Users,
      title: "Team Leadership",
      description: "Led cross-functional teams of up to 10 members",
      color: "from-green-400 to-green-600", 
      glow: "shadow-green-500/20"
    },
    {
      icon: Award,
      title: "Enterprise Excellence",
      description: "Built around 3 enterprise-grade applications and 8+ mid-scale applications",
      color: "from-purple-400 to-purple-600",
      glow: "shadow-purple-500/20"
    },
    {
      icon: Lightbulb,
      title: "AI/ML Innovation",
      description: "Core ML & NLP integration pioneer",
      color: "from-yellow-400 to-orange-600",
      glow: "shadow-orange-500/20"
    },
    {
      icon: Target,
      title: "Performance Focus",
      description: "Memory optimization & scalability expert",
      color: "from-red-400 to-red-600",
      glow: "shadow-red-500/20"
    },
    {
      icon: Rocket,
      title: "Innovation Driver",
      description: "POC development & emerging tech integration",
      color: "from-indigo-400 to-indigo-600",
      glow: "shadow-indigo-500/20"
    },
    {
      icon: Bot,
      title: "Agentic AI Fluency",
      description: "Fluent with Claude, Copilot & ChatGPT — from agentic coding to LLM-powered builds",
      color: "from-cyan-400 to-teal-600",
      glow: "shadow-cyan-500/20"
    }
  ];

  return (
    <motion.section 
      id="about" 
      ref={sectionRef} 
      className={`min-h-screen py-20 ${background.section} relative overflow-hidden transition-all duration-500`}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <motion.div 
          className={`absolute top-1/4 left-10 w-2 h-32 bg-blue-500 rounded-full rotate-45`}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className={`absolute bottom-1/4 right-16 w-2 h-24 bg-green-500 rounded-full -rotate-45`}
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.8, 0.3]
          }}
          transition={{ 
            duration: 4,
            delay: 1,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className={`absolute top-1/2 right-1/3 w-1 h-16 bg-purple-500 rounded-full rotate-12`}
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.9, 0.4]
          }}
          transition={{ 
            duration: 2.5,
            delay: 0.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-20"
            variants={itemVariants}
          >
            <motion.div 
              className={`inline-block px-4 py-2 ${background.card} rounded-full ${accents.about} font-mono text-sm mb-6`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              &gt; whoami
            </motion.div>
            <motion.h2 
              className={`text-5xl lg:text-7xl font-bold ${text.gradient} mb-6 leading-relaxed`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              About Me
            </motion.h2>
            <motion.p 
              className={`text-xl ${text.secondary} max-w-3xl mx-auto leading-relaxed`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Principal Software Engineer with a passion for crafting elegant, scalable solutions
              that push the boundaries of what's possible on Apple platforms and on the Web.
            </motion.p>
          </motion.div>

          {/* Main Content Grid */}
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-start mb-20"
            variants={itemVariants}
          >
            {/* Left Column - Profile Image and Description */}
            <motion.div 
              className="space-y-8"
              variants={itemVariants}
            >
              {/* Professional Profile Photo */}
              <motion.div 
                className="relative group"
                variants={cardHoverVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <div className={`${background.card} rounded-2xl p-6 pt-16 pb-8 shadow-2xl hover:shadow-blue-500/10 transition-all duration-500`}>
                  <div className="text-center">
                    <div className="relative inline-block">
                      <motion.div 
                        className="w-48 h-48 mx-auto rounded-2xl shadow-2xl shadow-blue-500/20 group-hover:shadow-blue-500/40 transition-all duration-300"
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'visible' }}
                      >
                        <img 
                          src="/rahul-singh-profile.jpg"
                          alt="Rahul Singh - Principal Software Engineer"
                          className="w-full h-full object-cover object-top rounded-2xl group-hover:scale-105 transition-transform duration-500 origin-center"
                        />
                      </motion.div>
                      <motion.div 
                        className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 border-4 border-white rounded-full"
                        animate={{ 
                          scale: [1, 1.2, 1],
                          opacity: [0.7, 1, 0.7]
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    </div>
                    <div className="mt-4 pb-2">
                      <h3 className={`text-xl font-bold ${text.primary} mb-2 leading-normal`} style={{lineHeight: '1.4'}}>Rahul Singh</h3>
                      <p className={`${accents.about} text-sm font-mono leading-relaxed`}>{profileInfo.title}</p>
                      <p className={`${text.muted} text-xs mt-1 leading-relaxed`}>{profileInfo.company} • {profileInfo.location}</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Profile Description */}
              <motion.div 
                className={`space-y-6 text-lg ${text.secondary} leading-relaxed`}
                variants={itemVariants}
              >
                <motion.p 
                  className="relative"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                >
                  <span className={`text-2xl ${accents.about} font-mono absolute -left-8 -top-2`}>"</span>
                  I'm a Principal Software Engineer at OpenText, with {experienceFormats.withOver} of dedicated innovation in the Apple ecosystem—building and scaling enterprise-grade iOS and macOS applications used by millions globally.
                </motion.p>
                
                <motion.p
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                >
                  I thrive at the intersection of complexity and clarity, translating challenging business needs into seamless, high‑performance mobile experiences. Whether crafting SwiftUI interfaces, integrating Core ML-powered features, or ensuring code quality through clean architecture and SOLID principles, my goal is always the same: deliver intuitive solutions that drive real-world impact.
                </motion.p>

                <motion.p
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0, duration: 0.8 }}
                >
                  Leadership is at the core of my approach. I guide cross-functional teams and mentor developers towards technical excellence while championing industry‑leading practices—from CI/CD pipelines and code reviews to robust, maintainable architectures. I believe in creating resilient systems that anticipate change—delivering not just features, but lasting value.
                </motion.p>

                <motion.p 
                  className="relative"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2, duration: 0.8 }}
                >
                  Fueling this journey is my passion for staying at the forefront of Apple's platform innovations and mentoring the next generation of engineers. Let's connect if you'd like to talk about architecture, mobile strategy, or pushing the boundaries of what's possible in enterprise applications.
                  <span className={`text-2xl ${accents.about} font-mono absolute -right-4 -bottom-2`}>"</span>
                </motion.p>
              </motion.div>

              {/* Key Stats - Now shows on all screen sizes beneath description */}
              <motion.div 
                className={`grid grid-cols-2 ${totalPatents >= 1 ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-6 py-8`}
                variants={containerVariants}
              >
                <motion.div 
                  className="text-center"
                  variants={statsCounterVariants}
                >
                  <motion.div 
                    className={`relative text-4xl font-bold mb-2 ${
                      isDark 
                        ? 'text-cyan-400' 
                        : 'text-indigo-600'
                    }`}
                    style={{
                      textShadow: isDark
                        ? '0 0 20px rgba(34, 211, 238, 0.6), 0 4px 8px rgba(0, 0, 0, 0.3)'
                        : '0 0 20px rgba(79, 70, 229, 0.6), 0 4px 8px rgba(0, 0, 0, 0.2)',
                      filter: isDark 
                        ? 'drop-shadow(0 2px 8px rgba(34, 211, 238, 0.4))'
                        : 'drop-shadow(0 2px 8px rgba(79, 70, 229, 0.4))'
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      delay: 0.8,
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    <span className="relative">
                      {experienceFormats.statsDisplay}
                      {/* Glossy overlay */}
                      <span 
                        className="absolute inset-0 bg-gradient-to-t from-transparent via-white/30 to-white/60 bg-clip-text text-transparent opacity-50"
                        style={{
                          WebkitMaskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)',
                          maskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)'
                        }}
                      >
                        {experienceFormats.statsDisplay}
                      </span>
                    </span>
                  </motion.div>
                  <div className={`${text.muted} text-sm uppercase tracking-wide`}>Years Experience</div>
                </motion.div>
                <motion.div 
                  className="text-center"
                  variants={statsCounterVariants}
                >
                  <motion.div 
                    className={`relative text-4xl font-bold mb-2 ${
                      isDark 
                        ? 'text-blue-400' 
                        : 'text-blue-600'
                    }`}
                    style={{
                      textShadow: isDark
                        ? '0 0 20px rgba(59, 130, 246, 0.6), 0 4px 8px rgba(0, 0, 0, 0.3)'
                        : '0 0 20px rgba(37, 99, 235, 0.6), 0 4px 8px rgba(0, 0, 0, 0.2)',
                      filter: isDark 
                        ? 'drop-shadow(0 2px 8px rgba(59, 130, 246, 0.4))'
                        : 'drop-shadow(0 2px 8px rgba(37, 99, 235, 0.4))'
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      delay: 1.0,
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    <span className="relative">
                      {applicationsDeployed}
                      {/* Glossy overlay */}
                      <span 
                        className="absolute inset-0 bg-gradient-to-t from-transparent via-white/30 to-white/60 bg-clip-text text-transparent opacity-50"
                        style={{
                          WebkitMaskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)',
                          maskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)'
                        }}
                      >
                        {applicationsDeployed}
                      </span>
                    </span>
                  </motion.div>
                  <div className={`${text.muted} text-sm uppercase tracking-wide`}>Applications</div>
                </motion.div>
                {totalPatents >= 1 && (
                <motion.div 
                  className="text-center"
                  variants={statsCounterVariants}
                >
                  <motion.div 
                    className={`relative text-4xl font-bold mb-2 ${
                      isDark 
                        ? 'text-amber-400' 
                        : 'text-amber-600'
                    }`}
                    style={{
                      textShadow: isDark
                        ? '0 0 20px rgba(245, 158, 11, 0.6), 0 4px 8px rgba(0, 0, 0, 0.3)'
                        : '0 0 20px rgba(217, 119, 6, 0.6), 0 4px 8px rgba(0, 0, 0, 0.2)',
                      filter: isDark 
                        ? 'drop-shadow(0 2px 8px rgba(245, 158, 11, 0.4))'
                        : 'drop-shadow(0 2px 8px rgba(217, 119, 6, 0.4))'
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      delay: 1.1,
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    <span className="relative">
                      {totalPatents}
                      <span 
                        className="absolute inset-0 bg-gradient-to-t from-transparent via-white/30 to-white/60 bg-clip-text text-transparent opacity-50"
                        style={{
                          WebkitMaskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)',
                          maskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)'
                        }}
                      >
                        {totalPatents}
                      </span>
                    </span>
                  </motion.div>
                  <div className={`${text.muted} text-sm uppercase tracking-wide`}>{totalPatents === 1 ? 'Patent' : 'Patents'}</div>
                </motion.div>
                )}
                <motion.div 
                  className="text-center"
                  variants={statsCounterVariants}
                >
                  <motion.div 
                    className={`relative text-4xl font-bold mb-2 ${
                      isDark 
                        ? 'text-green-400' 
                        : 'text-green-600'
                    }`}
                    style={{
                      textShadow: isDark
                        ? '0 0 20px rgba(34, 197, 94, 0.6), 0 4px 8px rgba(0, 0, 0, 0.3)'
                        : '0 0 20px rgba(21, 128, 61, 0.6), 0 4px 8px rgba(0, 0, 0, 0.2)',
                      filter: isDark 
                        ? 'drop-shadow(0 2px 8px rgba(34, 197, 94, 0.4))'
                        : 'drop-shadow(0 2px 8px rgba(21, 128, 61, 0.4))'
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      delay: 1.2,
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    <span className="relative">
                      {totalCertifications}
                      {/* Glossy overlay */}
                      <span 
                        className="absolute inset-0 bg-gradient-to-t from-transparent via-white/30 to-white/60 bg-clip-text text-transparent opacity-50"
                        style={{
                          WebkitMaskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)',
                          maskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)'
                        }}
                      >
                        {totalCertifications}
                      </span>
                    </span>
                  </motion.div>
                  <div className={`${text.muted} text-sm uppercase tracking-wide`}>Certification</div>
                </motion.div>
                <motion.div 
                  className="text-center"
                  variants={statsCounterVariants}
                >
                  <motion.div 
                    className={`relative text-4xl font-bold mb-2 ${
                      isDark 
                        ? 'text-purple-400' 
                        : 'text-purple-600'
                    }`}
                    style={{
                      textShadow: isDark
                        ? '0 0 20px rgba(168, 85, 247, 0.6), 0 4px 8px rgba(0, 0, 0, 0.3)'
                        : '0 0 20px rgba(147, 51, 234, 0.6), 0 4px 8px rgba(0, 0, 0, 0.2)',
                      filter: isDark 
                        ? 'drop-shadow(0 2px 8px rgba(168, 85, 247, 0.4))'
                        : 'drop-shadow(0 2px 8px rgba(147, 51, 234, 0.4))'
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      delay: 1.4,
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                  >
                    <span className="relative">
                      {totalAwards}
                      {/* Glossy overlay */}
                      <span 
                        className="absolute inset-0 bg-gradient-to-t from-transparent via-white/30 to-white/60 bg-clip-text text-transparent opacity-50"
                        style={{
                          WebkitMaskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)',
                          maskImage: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0.4) 60%, transparent 100%)'
                        }}
                      >
                        {totalAwards}
                      </span>
                    </span>
                  </motion.div>
                  <div className={`${text.muted} text-sm uppercase tracking-wide`}>Awards</div>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Right Column - Code Block */}
            <motion.div 
              className="space-y-8"
              variants={itemVariants}
            >
              {/* Code Block */}
              <motion.div 
                className="relative"
                variants={cardHoverVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <div className={`${background.card} rounded-2xl p-6 shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 group overflow-hidden`}>
                  <div className="flex items-center space-x-2 mb-4">
                    <motion.div 
                      className="w-3 h-3 bg-red-500 rounded-full"
                      whileHover={{ scale: 1.2 }}
                    />
                    <motion.div 
                      className="w-3 h-3 bg-yellow-500 rounded-full"
                      whileHover={{ scale: 1.2 }}
                    />
                    <motion.div 
                      className="w-3 h-3 bg-green-500 rounded-full"
                      whileHover={{ scale: 1.2 }}
                    />
                    <span className={`${text.primary} text-sm font-mono font-bold ml-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent`}>RahulSingh.swift</span>
                  </div>
                  <motion.div 
                    className="w-full"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 1.2 }}
                  >
                    <pre className={`text-xs lg:text-sm ${text.secondary} font-mono leading-relaxed group-hover:${text.primary} transition-colors duration-300 whitespace-pre-wrap break-words pb-2`}
                         style={{lineHeight: '1.5'}}>
                      {renderSwiftCode(swiftCode, xcodeColors)}
                    </pre>
                  </motion.div>
                </div>
                
                {/* Floating elements */}
                <motion.div 
                  className="absolute -top-4 -right-4 w-8 h-8 bg-blue-500/80 rounded-lg shadow-lg shadow-blue-500/50"
                  animate={{ 
                    y: [0, -10, 0],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <motion.div 
                  className="absolute -bottom-4 -left-4 w-6 h-6 bg-green-500/80 rounded-lg shadow-lg shadow-green-500/50"
                  animate={{ 
                    y: [0, -8, 0],
                    x: [0, 4, 0]
                  }}
                  transition={{ 
                    duration: 2.5,
                    delay: 1,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Highlights Grid */}
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
          >
            {highlights.map((item, index) => {
              const Icon = item.icon;
              const isLastOnMd = index === highlights.length - 1 && highlights.length % 2 === 1;
              const isLastOnLg = index === highlights.length - 1 && highlights.length % 3 === 1;
              return (
                <motion.div
                  key={index}
                  ref={el => cardsRef.current[index] = el}
                  className={`group relative w-full ${isLastOnMd ? 'md:col-span-2 md:max-w-sm md:mx-auto' : ''} ${isLastOnLg ? 'lg:col-span-1 lg:col-start-2 lg:max-w-none lg:mx-0' : ''}`}
                  variants={itemVariants}
                  custom={index}
                  whileHover={{ 
                    scale: 1.05,
                    rotateY: 5,
                    transition: { duration: 0.3 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <div className={`relative ${background.card} rounded-2xl p-6 shadow-2xl ${item.glow} hover:scale-105 transition-all duration-500 overflow-hidden`}>
                    {/* Background Gradient */}
                    <motion.div 
                      className={`absolute inset-0 bg-gradient-to-br ${item.color} rounded-2xl`}
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 0.1 }}
                      transition={{ duration: 0.5 }}
                    />
                    
                    {/* Icon */}
                    <div className="relative z-10 mb-4">
                      <motion.div 
                        className={`inline-flex p-3 bg-gradient-to-br ${item.color} rounded-xl shadow-lg ${item.glow}`}
                        whileHover={{ 
                          scale: 1.1,
                          rotate: 5
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </motion.div>
                    </div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <motion.h3 
                        className={`text-xl font-bold ${text.primary} mb-2 group-hover:${text.primary} transition-colors`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                      >
                        {item.title}
                      </motion.h3>
                      <motion.p 
                        className={`${text.muted} text-sm leading-relaxed group-hover:${text.secondary} transition-colors`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                      >
                        {item.description}
                      </motion.p>
                    </div>
                    
                    {/* Hover Effect Lines */}
                    <motion.div 
                      className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-${isDark ? 'white/20' : 'gray-400/40'} to-transparent`}
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.5 }}
                    />
                    <motion.div 
                      className={`absolute bottom-0 right-0 w-0.5 h-full bg-gradient-to-t from-transparent via-${isDark ? 'white/20' : 'gray-400/40'} to-transparent`}
                      initial={{ scaleY: 0 }}
                      whileHover={{ scaleY: 1 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default AboutSection;